import type { InputConfig } from '../config/input.config';
import { HOLD_RELEASE, InputAction } from './InputAction';
import type { ActionSink, InputSource } from './InputSource';

interface Gesture {
  readonly startX: number;
  readonly startY: number;
  readonly startTime: number;
  fired: boolean;
  /** Release action owed when the finger lifts (swipe down = slide while held). */
  release: InputAction | null;
  /** While a held slide lasts: where the finger was at the last lane change – sideways moves from here steer. */
  anchorX: number;
}

/**
 * Swipe input for touch devices (also works with mouse drag on desktop,
 * handy for testing). Swipes fire as soon as the threshold is crossed –
 * not on release – which feels much more responsive.
 * Second swipe up while airborne = double jump (handled by the controller).
 * Listens on the window so taps on overlays (start/game-over screen) count
 * too; interactive UI elements stop propagation of their pointerdown.
 */
export class TouchInputSource implements InputSource {
  private readonly gestures = new Map<number, Gesture>();
  private sink: ActionSink | null = null;
  /**
   * Chrome delivers `pointermove` once per frame (lined up with the next frame); `pointerrawupdate` arrives as the
   * finger moves – a swipe fires up to a frame earlier, which is noticeable on phones at low frame rates (user
   * 2026-10-03: steering felt late). Other browsers keep `pointermove`.
   */
  private readonly moveEvent: 'pointerrawupdate' | 'pointermove';

  constructor(
    private readonly target: Window,
    private readonly config: InputConfig['swipe'],
  ) {
    this.moveEvent = 'onpointerrawupdate' in target ? 'pointerrawupdate' : 'pointermove';
  }

  attach(sink: ActionSink): void {
    this.sink = sink;
    this.target.addEventListener('pointerdown', this.onDown);
    this.target.addEventListener(this.moveEvent, this.onMove as EventListener);
    this.target.addEventListener('pointerup', this.onUp);
    this.target.addEventListener('pointercancel', this.onCancel);
  }

  detach(): void {
    this.target.removeEventListener('pointerdown', this.onDown);
    this.target.removeEventListener(this.moveEvent, this.onMove as EventListener);
    this.target.removeEventListener('pointerup', this.onUp);
    this.target.removeEventListener('pointercancel', this.onCancel);
    this.gestures.clear();
    this.sink = null;
  }

  private threshold(): number {
    const shortEdge = Math.min(window.innerWidth, window.innerHeight);
    return Math.max(this.config.minDistancePx, shortEdge * this.config.minDistanceFraction);
  }

  private readonly onDown = (event: PointerEvent): void => {
    this.gestures.set(event.pointerId, { startX: event.clientX, startY: event.clientY, startTime: event.timeStamp, fired: false, release: null, anchorX: event.clientX });
  };

  private readonly onMove = (event: PointerEvent): void => {
    const gesture = this.gestures.get(event.pointerId);
    if (!gesture) return;
    if (gesture.fired) {
      // Held slide (finger still down after the swipe): moving sideways changes lanes, the slide goes on
      // (user 2026-10-03) – each further threshold of sideways travel is one lane, no time limit while held.
      if (gesture.release === null) return;
      const sideways = event.clientX - gesture.anchorX;
      if (Math.abs(sideways) < this.threshold()) return;
      gesture.anchorX = event.clientX;
      this.sink?.(sideways < 0 ? InputAction.MoveLeft : InputAction.MoveRight);
      return;
    }
    if (event.timeStamp - gesture.startTime > this.config.maxDurationMs) return;
    const dx = event.clientX - gesture.startX;
    const dy = event.clientY - gesture.startY;
    if (Math.hypot(dx, dy) < this.threshold()) return;
    gesture.fired = true;
    const action = Math.abs(dx) > Math.abs(dy) ? (dx < 0 ? InputAction.MoveLeft : InputAction.MoveRight) : dy < 0 ? InputAction.Jump : InputAction.Slide;
    gesture.release = HOLD_RELEASE[action] ?? null;
    gesture.anchorX = event.clientX;
    this.sink?.(action);
  };

  private readonly onUp = (event: PointerEvent): void => {
    const gesture = this.gestures.get(event.pointerId);
    this.gestures.delete(event.pointerId);
    if (!gesture) return;
    if (gesture.release) this.sink?.(gesture.release);
    else if (!gesture.fired) this.sink?.(InputAction.Confirm);
  };

  private readonly onCancel = (event: PointerEvent): void => {
    const gesture = this.gestures.get(event.pointerId);
    this.gestures.delete(event.pointerId);
    if (gesture?.release) this.sink?.(gesture.release);
  };
}
